import React, {useEffect, useState} from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import {DEFAULT_SETTINGS, PlayerSettings, Settings, SENSITIVITY_MAX, SENSITIVITY_MIN} from "../../settings";
import {QualityChoice, effectiveQuality, isQualityChoice} from "../../quality";
import {InputModeChoice, isInputModeChoice} from "../../inputMode";
import {InputModeState, useInputMode} from "../../inputModeState";
import "./SettingsPanel.scss";

// sliders work on a log2 scale so 0.25x..4x is symmetric around 1x
const toSlider = (v: number) => Math.log2(v);
const fromSlider = (x: number) => Math.round(Math.pow(2, x) * 100) / 100;

function SensitivityRow(props: {id: string, label: string, help: string, value: number, onChange: (v: number) => void}) {
    return <div className="settings-row">
        <label htmlFor={props.id}>{props.label} <span className="value" id={props.id + "-value"}>{props.value.toFixed(2)}×</span></label>
        <input id={props.id} type="range" className="form-range"
               min={toSlider(SENSITIVITY_MIN)} max={toSlider(SENSITIVITY_MAX)} step={0.05}
               value={toSlider(props.value)}
               onChange={e => props.onChange(fromSlider(Number(e.target.value)))}/>
        <div className="scale"><span>{SENSITIVITY_MIN}×</span><span>1×</span><span>{SENSITIVITY_MAX}×</span></div>
        <small className="help">{props.help}</small>
    </div>;
}

const LEVEL_NAME = {low: "Low", high: "High"};

function QualityRow({settings: s}: {settings: Settings}) {
    const level = effectiveQuality(s);
    const auto = s.quality !== "auto" ? null
        : s.detectedQuality ? `Auto chose ${LEVEL_NAME[s.detectedQuality]} for this device.` : "Auto: measuring this device's frame rate (High until then).";
    return <div className="settings-row">
        <label htmlFor="graphics-quality">Graphics quality <span className="value" id="graphics-quality-value">{LEVEL_NAME[level]}</span></label>
        <Form.Select id="graphics-quality" size="sm" value={s.quality}
                     onChange={e => { const v = e.target.value; if (isQualityChoice(v)) PlayerSettings.set({quality: v as QualityChoice}); }}>
            <option value="auto">Auto (by frame rate)</option>
            <option value="high">High</option>
            <option value="low">Low (faster)</option>
        </Form.Select>
        <small className="help">
            {auto && <span id="graphics-quality-auto">{auto} </span>}
            High: full detail. Low: simpler room and table, smaller textures, for slow computers and phones (Auto starts at Low on a phone). Applies at once;
            edge smoothing (antialiasing) follows on the next page load.
        </small>
    </div>;
}

const MODE_NAME = {desktop: "Desktop", touch: "Touch"};

function ControlsRow({settings: s}: {settings: Settings}) {
    const mode = useInputMode();
    const detected = InputModeState.detected();
    return <div className="settings-row">
        <label htmlFor="input-mode">Controls <span className="value" id="input-mode-value">{MODE_NAME[mode]}</span></label>
        <Form.Select id="input-mode" size="sm" value={s.inputMode}
                     onChange={e => { const v = e.target.value; if (isInputModeChoice(v)) PlayerSettings.set({inputMode: v as InputModeChoice}); }}>
            <option value="auto">Auto (this device: {MODE_NAME[detected]})</option>
            <option value="desktop">Desktop (mouse and keyboard)</option>
            <option value="touch">Touch (on-screen controls)</option>
        </Form.Select>
        <small className="help">
            Desktop: move the mouse to look, hold Space and stroke the mouse. Touch: drag to look, hold the HOLD button
            (or keep one finger down and add a second) and stroke with a finger: drag down, then push up through the ball.
        </small>
    </div>;
}

export default function SettingsPanel(props: {show: boolean, onHide: () => void}) {
    const [s, setS] = useState<Settings>(PlayerSettings.get());
    const touch = useInputMode() === "touch";
    useEffect(() => PlayerSettings.subscribe(setS), []);
    const isDefault = s.aimSensitivity === DEFAULT_SETTINGS.aimSensitivity && s.strokeSensitivity === DEFAULT_SETTINGS.strokeSensitivity
        && s.showFps === DEFAULT_SETTINGS.showFps && s.quality === DEFAULT_SETTINGS.quality && s.inputMode === DEFAULT_SETTINGS.inputMode;
    return <Modal show={props.show} onHide={props.onHide} centered scrollable size="sm" id="settings-modal">
        <Modal.Header closeButton><Modal.Title>Settings</Modal.Title></Modal.Header>
        <Modal.Body>
            <SensitivityRow id="aim-sensitivity" label="Aim / look sensitivity" value={s.aimSensitivity}
                            help={touch ? "How fast the view turns when you drag a finger." : "How fast the view turns when you move the mouse."}
                            onChange={v => PlayerSettings.set({aimSensitivity: v})}/>
            <SensitivityRow id="stroke-sensitivity" label="Cue stroke sensitivity" value={s.strokeSensitivity}
                            help={touch ? "How far the cue moves per finger movement while the aim is locked. Higher also means more power for the same finger speed."
                                  : "How far the cue moves per mouse movement while Space is held. Higher also means more power for the same hand speed."}
                            onChange={v => PlayerSettings.set({strokeSensitivity: v})}/>
            <div className="settings-row">
                <Form.Check type="switch" id="show-fps" className="settings-switch" label="Show FPS counter"
                            checked={s.showFps} onChange={e => PlayerSettings.set({showFps: e.target.checked})}/>
                <small className="help">Frames per second in the top-left corner of the game.</small>
            </div>
            <QualityRow settings={s}/>
            <ControlsRow settings={s}/>
        </Modal.Body>
        <Modal.Footer>
            <Button variant="outline-secondary" id="btn-settings-reset" disabled={isDefault} onClick={() => PlayerSettings.reset()}>Reset to defaults</Button>
            <Button variant="primary" id="btn-settings-close" onClick={props.onHide}>Done</Button>
        </Modal.Footer>
    </Modal>;
}
